/**
 * Stream-specific utility functions
 */
// Removed stream status config imports - using string literals
import useThreadStore from "@/store/threadStore";

// Batching mechanism for state updates
const stateUpdateQueue = new Map();
const BATCH_DELAY = 100; // 100ms (~10fps) to prevent browser crashes during web search streaming while maintaining smooth updates
let batchTimeout = null;

// Track which messages have been logged to prevent spam
const loggedThreadMismatches = new Set();

/**
 * Process queued state updates in batches
 * @param {Function} updateMessageState - Function to update message state
 */
function processBatchedUpdates(updateMessageState) {
  if (stateUpdateQueue.size === 0) return;

  // Get current thread context to validate updates
  const currentState = useThreadStore.getState();
  const { currentThreadId } = currentState;

  // Group updates by message ID
  const updatesByMessage = new Map();

  for (const [key, update] of stateUpdateQueue) {
    const messageId = update.id;
    if (!updatesByMessage.has(messageId)) {
      updatesByMessage.set(messageId, {});
    }

    // Merge all updates for this message
    Object.assign(updatesByMessage.get(messageId), update);
  }

  // Apply batched updates with thread validation
  for (const [messageId, batchedUpdate] of updatesByMessage) {
    // THREAD VALIDATION: Verify message belongs to current thread before updating
    // Skip update if:
    // 1. Message belongs to a thread, but user is now on /chat/new (currentThreadId is null)
    // 2. Message belongs to thread A, but user is now on thread B
    if (batchedUpdate.thread_id) {
      if (!currentThreadId || batchedUpdate.thread_id !== currentThreadId) {
        // Still update threadMessages Map for background streaming (handled in updateMessageState)
        // But this validation prevents the update from appearing in the messages array

        // Log only once per message to avoid spam
        const logKey = `${messageId}-${batchedUpdate.thread_id}-${currentThreadId}`;
        if (import.meta.env.DEV && !loggedThreadMismatches.has(logKey)) {
          console.log(
            "[streamUtils] Background stream detected - message updates for different thread",
            {
              messageId,
              messageThread: batchedUpdate.thread_id,
              currentThread: currentThreadId,
            }
          );
          loggedThreadMismatches.add(logKey);

          // Clean up old entries after 30 seconds
          setTimeout(() => loggedThreadMismatches.delete(logKey), 30000);
        }
        continue;
      }
    }

    updateMessageState(batchedUpdate);
  }

  // Clear the queue
  stateUpdateQueue.clear();
  batchTimeout = null;
}

/**
 * Safely updates a streaming message without risking database conflicts
 * Enhanced with batching for better performance
 * @param {Function} updateMessageState - Function to update message state
 * @param {string} messageId - ID of the message being updated
 * @param {string} content - New content for the message
 * @param {string} status - New status for the message
 * @param {boolean} forceBatch - Whether to force batching (default: true for streaming)
 * @param {Object} metadata - Optional metadata to include (e.g., reasoning content)
 * @param {string} threadId - Thread ID to prevent race conditions
 */
export function safelyUpdateStreamingMessage(
  updateMessageState,
  messageId,
  content,
  status = "streaming",
  forceBatch = true,
  metadata = {},
  threadId = null,
  role = null // Allow role to be specified, don't default to assistant
) {
  if (!updateMessageState || !messageId) return;

  // Get existing message metadata from thread store to merge
  // This ensures toolSelectionContext and systemPrompt are preserved during streaming
  let existingMetadata = {};
  try {
    // Get existing metadata from the thread store
    if (useThreadStore?.getState) {
      const threadStore = useThreadStore.getState();
      const existingMessage = threadStore?.messages?.find(
        (m) => m.id === messageId
      );
      if (existingMessage?.metadata) {
        existingMetadata = existingMessage.metadata;
      }
    }
  } catch (e) {
    // If we can't get existing metadata (e.g., store not available), continue with just the new metadata
    // This is expected in some contexts where the store isn't accessible
  }

  const update = {
    id: messageId,
    // Only include content if it's provided (allow metadata-only updates for tool results)
    ...(content !== undefined ? { content: content } : {}),
    isStreaming: status === "streaming",
    messageStatus: status,
    skipDatabaseSave: true, // Always set to true for streaming updates
    // Merge existing metadata with new metadata (new metadata takes precedence)
    // This preserves toolSelectionContext and systemPrompt during streaming updates
    ...((metadata && Object.keys(metadata).length > 0) ||
    Object.keys(existingMetadata).length > 0
      ? {
          metadata: {
            ...existingMetadata,
            ...metadata,
          },
        }
      : {}),
    // RACE CONDITION FIX: Include thread_id to prevent updates to wrong thread
    ...(threadId ? { thread_id: threadId } : {}),
    // CRITICAL FIX: Only set role if explicitly provided AND validate it's not corrupting a user message
    ...(role ? { role } : {}),
  };

  // CRITICAL FIX: Add role validation to prevent user message corruption
  if (role && role !== "user") {
    // console.log("🔍 [safelyUpdateStreamingMessage] Role validation:", {
    //   messageId,
    //   proposedRole: role,
    //   status,
    //   contentPreview: content?.substring(0, 50) || "no content",
    // });
    // Additional validation could be added here if needed
    // For now, we trust that the calling code has the correct role
  }

  // For streaming updates, use batching to improve performance
  if (forceBatch && status === "streaming") {
    const updateKey = `${messageId}-${Date.now()}`;
    stateUpdateQueue.set(updateKey, update);

    // Schedule batch processing
    if (!batchTimeout) {
      batchTimeout = setTimeout(() => {
        processBatchedUpdates(updateMessageState);
      }, BATCH_DELAY);
    }
  } else {
    // For final updates or when batching is disabled, update immediately
    updateMessageState(update);
  }
}

/**
 * Force flush any pending batched updates
 * @param {Function} updateMessageState - Function to update message state
 */
export function flushBatchedUpdates(updateMessageState) {
  if (batchTimeout) {
    clearTimeout(batchTimeout);
    processBatchedUpdates(updateMessageState);
  }
}

/**
 * Generates a UUID for message IDs
 * @returns {string} - A UUID string
 */
export function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
